import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, isWorkspaceMismatch } from "@/lib/api";
import { freeTrialBlurb, starterScansPhrase, planNumbers } from "@/lib/planCopy";
import { useAuth } from "@/contexts/AuthContext";
import {
  TrendingUp,
  Zap,
  Award,
  Radar,
  ArrowUpRight,
  ArrowRight,
  Sparkles,
  Building2,
  Search,
  Bookmark,
  PlayCircle,
  HeartPulse,
  LineChart,
  Handshake,
  CalendarDays,
  ChevronDown,
  Loader2,
} from "lucide-react";
import WelcomeCard from "@/components/WelcomeCard";

// ------- tiny SVG chart primitives (no external chart lib needed) -------

function SparkArea({ data, color = "#1F4A38", height = 56, className = "" }) {
  const id = React.useId();
  if (!data?.length) return null;
  const w = 200;
  const h = height;
  const max = Math.max(...data, 1);
  const step = w / (data.length - 1 || 1);
  const points = data.map((v, i) => [i * step, h - (v / max) * (h - 6) - 3]);
  const line = points.map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`)).join(" ");
  const area = `${line} L ${w} ${h} L 0 ${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`w-full h-full ${className}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`spark-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#spark-${id})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BarSeries({ data, className = "" }) {
  const [hover, setHover] = React.useState(null);
  if (!data?.length) return null;
  const max = Math.max(...data.map((d) => d.v), 1);
  return (
    <div className={`flex h-full gap-2.5 ${className}`}>
      {/* Y axis — leads count */}
      <div className="flex flex-col justify-between text-[9px] font-mono text-lm-text3 shrink-0 text-right pb-[18px]">
        <span>{max}</span>
        <span>{Math.round(max / 2)}</span>
        <span>0</span>
      </div>
      {/* bars + gridlines */}
      <div className="flex-1 flex items-end justify-between gap-2 relative">
        <div className="absolute left-0 right-0 top-0 bottom-[18px] flex flex-col justify-between pointer-events-none">
          <div className="border-t border-dashed border-lm-border/60" />
          <div className="border-t border-dashed border-lm-border/60" />
          <div className="border-t border-dashed border-lm-border/60" />
        </div>
        {data.map((d, i) => {
        const pct = (d.v / max) * 100;
        const on = hover === i;
        return (
          <div
            key={i}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            className="flex-1 h-full flex flex-col items-center justify-end gap-2 min-w-0 cursor-default"
          >
            <div className="w-full flex-1 flex items-end min-h-0 relative">
              {on && (
                <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10 whitespace-nowrap rounded-md bg-[color:var(--lm-dark)] text-white text-[10px] font-mono px-2 py-1 shadow-lg pointer-events-none">
                  {d.v} lead{d.v === 1 ? "" : "s"} · {d.label}
                </div>
              )}
              <div
                className={`w-full rounded-t-md transition-all ${on ? "bg-lm-primary brightness-110" : "bg-gradient-to-t from-lm-primary to-[#3E7A5B]"}`}
                style={{ height: `${Math.max(pct, 6)}%` }}
              />
            </div>
            <span className={`text-[10px] font-mono truncate w-full text-center transition-colors ${on ? "text-lm-text font-semibold" : "text-lm-text3"}`}>{d.label}</span>
          </div>
        );
      })}
      </div>
    </div>
  );
}

function AreaLineChart({ points, className = "" }) {
  const [hi, setHi] = React.useState(null);
  if (!points?.length) return null;
  const w = 640;
  const h = 240;
  const pad = { l: 44, r: 12, t: 14, b: 24 };
  const iw = w - pad.l - pad.r;
  const ih = h - pad.t - pad.b;
  const max = Math.max(...points.map((p) => p.v), 1);
  const step = iw / (points.length - 1 || 1);
  const coords = points.map((p, i) => [pad.l + i * step, pad.t + ih - (p.v / max) * ih]);
  const line = coords.map((c, i) => (i === 0 ? `M ${c[0]} ${c[1]}` : `L ${c[0]} ${c[1]}`)).join(" ");
  const area = `${line} L ${pad.l + iw} ${pad.t + ih} L ${pad.l} ${pad.t + ih} Z`;
  const gridLines = [0.25, 0.5, 0.75, 1].map((r) => pad.t + ih * (1 - r));
  const yTicks = [0.25, 0.5, 0.75, 1].map((r) => Math.round((max * r) / 10) * 10);
  const labelX = hi != null ? Math.min(Math.max(coords[hi][0], pad.l + 26), w - pad.r - 26) : 0;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`w-full h-full ${className}`} preserveAspectRatio="none" onMouseLeave={() => setHi(null)}>
      <defs>
        <linearGradient id="area-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#1F4A38" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#1F4A38" stopOpacity="0" />
        </linearGradient>
      </defs>
      {gridLines.map((y, i) => (
        <g key={i}>
          <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="#E7E4D8" strokeWidth="1" strokeDasharray="3 4" />
          <text x={pad.l - 8} y={y + 3} textAnchor="end" className="fill-lm-text3" fontSize="10" fontFamily="ui-monospace, monospace">
            {yTicks[i]}
          </text>
        </g>
      ))}
      <path d={area} fill="url(#area-fill)" />
      <path d={line} fill="none" stroke="#1F4A38" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* hover guide + active point + value bubble */}
      {hi != null && (
        <g style={{ pointerEvents: "none" }}>
          <line x1={coords[hi][0]} x2={coords[hi][0]} y1={pad.t} y2={pad.t + ih} stroke="#1F4A38" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
          <circle cx={coords[hi][0]} cy={coords[hi][1]} r="5" fill="#1F4A38" stroke="#fff" strokeWidth="2" />
          {/* value bubble at the TOP of the stick */}
          <g transform={`translate(${labelX}, ${pad.t + 4})`}>
            <rect x="-30" y="-11" width="60" height="20" rx="6" fill="#123D2A" />
            <text x="0" y="3" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="700" fontFamily="ui-monospace, monospace">{points[hi].v}</text>
          </g>
        </g>
      )}
      {hi == null && coords.length > 0 && (
        <circle cx={coords[coords.length - 1][0]} cy={coords[coords.length - 1][1]} r="4" fill="#1F4A38" />
      )}
      {points.map((p, i) => (
        <text key={i} x={pad.l + i * step} y={h - 6} textAnchor="middle" className="fill-lm-text3" fontSize="10" fontFamily="ui-monospace, monospace">
          {p.label}
        </text>
      ))}
      {/* invisible hover bands */}
      {points.map((p, i) => (
        <rect
          key={`hb-${i}`}
          x={pad.l + i * step - step / 2}
          y={pad.t}
          width={step}
          height={ih}
          fill="transparent"
          style={{ cursor: "crosshair" }}
          onMouseEnter={() => setHi(i)}
        />
      ))}
    </svg>
  );
}

function Donut({ segments, size = 200, thickness = 26, activeIndex, onHoverSegment }) {
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2;
  const rInner = rOuter - thickness;
  const total = segments.reduce((a, s) => a + s.v, 0) || 1;
  const gapDeg = segments.length > 1 ? 2 : 0; // small gap between segments
  const totalGapDeg = gapDeg * segments.length;
  const drawableDeg = 360 - totalGapDeg;
  let startDeg = -90; // 12 o'clock

  const paths = segments.map((s, i) => {
    const sweep = (s.v / total) * drawableDeg;
    const path = describeDonutSlice(cx, cy, rOuter, rInner, startDeg, startDeg + sweep);
    startDeg += sweep + gapDeg;
    return { s, i, d: path };
  });

  const top = segments.slice().sort((a, b) => (b.v || 0) - (a.v || 0))[0];

  return (
    <div className="relative w-full h-full">
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full" role="img" aria-label="Source breakdown">
        {/* Background ring (visible when segments have gaps) */}
        <circle cx={cx} cy={cy} r={(rOuter + rInner) / 2} fill="none" stroke="#F3EFE1" strokeWidth={thickness - 4} />
        {paths.map(({ s, i, d }) => (
          <path
            key={i}
            d={d}
            fill={s.color}
            onMouseEnter={() => onHoverSegment && onHoverSegment(i)}
            onMouseLeave={() => onHoverSegment && onHoverSegment(null)}
            style={{
              transition: "opacity 0.2s ease, transform 0.2s ease",
              transformOrigin: `${cx}px ${cy}px`,
              opacity: activeIndex == null || activeIndex === i ? 1 : 0.35,
              cursor: onHoverSegment ? "pointer" : "default",
            }}
          />
        ))}
      </svg>
      {/* Center label — dynamic on hover, otherwise the top source */}
      {top && (() => {
        const cur = activeIndex != null ? segments[activeIndex] : top;
        return (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center px-6">
            <div className="text-[9px] font-mono uppercase tracking-widest text-lm-text3">
              {activeIndex != null ? "Share" : "Top source"}
            </div>
            <div className="text-sm font-semibold text-lm-text truncate max-w-full">{cur.label}</div>
            <div className="text-xl font-display font-semibold leading-none mt-0.5" style={{ color: cur.color }}>{cur.v}%</div>
          </div>
        );
      })()}
    </div>
  );
}

// SVG donut-slice path helper. Produces a solid wedge between two angles for
// the given outer/inner radii. Handles the >180° edge case with the largeArc
// flag correctly.
function polarToCartesian(cx, cy, r, deg) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}
function describeDonutSlice(cx, cy, rOuter, rInner, startDeg, endDeg) {
  const large = endDeg - startDeg > 180 ? 1 : 0;
  const oStart = polarToCartesian(cx, cy, rOuter, startDeg);
  const oEnd = polarToCartesian(cx, cy, rOuter, endDeg);
  const iEnd = polarToCartesian(cx, cy, rInner, endDeg);
  const iStart = polarToCartesian(cx, cy, rInner, startDeg);
  return [
    `M ${oStart.x} ${oStart.y}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${oEnd.x} ${oEnd.y}`,
    `L ${iEnd.x} ${iEnd.y}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${iStart.x} ${iStart.y}`,
    "Z",
  ].join(" ");
}

// Wrapper card content — donut on the left, keyed legend on the right, hover
// links both. Uses grid `auto | 1fr` so labels never touch the ring.
function SourceBreakdown({ segments }) {
  const fmtPct = (v) => {
    const n = Number(v) || 0;
    if (n >= 10 || n === 0) return `${Math.round(n)}%`;
    return `${n.toFixed(1)}%`;
  };
  const top = segments[0];
  return (
    <div>
      {/* Headline — the dominant source */}
      {top && (
        <div className="flex items-baseline gap-2 mb-4">
          <span className="w-3 h-3 rounded-full self-center shrink-0" style={{ background: top.color }} />
          <span className="text-3xl font-display font-bold text-lm-text leading-none tabular-nums">{fmtPct(top.v)}</span>
          <span className="text-sm text-lm-text2 truncate">from {top.label}</span>
        </div>
      )}
      {/* Stacked share bar */}
      <div className="flex h-2.5 rounded-full overflow-hidden mb-5 bg-lm-tint" role="img" aria-label="Source breakdown">
        {segments.map((s) => (
          <div key={s.label} style={{ width: `${s.v}%`, background: s.color }} title={`${s.label}: ${fmtPct(s.v)}`} />
        ))}
      </div>
      {/* Per-source rows with mini bars */}
      <ul className="space-y-1.5" role="list">
        {segments.map((s) => (
          <li key={s.label} className="group flex items-center gap-3 -mx-2 px-2 py-1.5 rounded-lg hover:bg-lm-tint/50 transition-colors cursor-default" title={`${s.label}: ${fmtPct(s.v)}`}>
            <span className="w-2.5 h-2.5 rounded-full shrink-0 transition-transform group-hover:scale-125" style={{ background: s.color }} />
            <span className="text-sm text-lm-text font-medium w-24 shrink-0 truncate">{s.label}</span>
            <div className="flex-1 h-1.5 rounded-full bg-lm-tint overflow-hidden">
              <div className="h-full rounded-full transition-all group-hover:brightness-110" style={{ width: `${s.v}%`, background: s.color }} />
            </div>
            <span className="font-mono text-xs font-semibold text-lm-text2 group-hover:text-lm-text w-12 text-right tabular-nums shrink-0 transition-colors">
              {fmtPct(s.v)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ------- Dashboard -------

const PLATFORM_INITIAL_BG = {
  reddit: "bg-[#FF4500]",
  x: "bg-[#111111]",
  twitter: "bg-[#111111]",
  linkedin: "bg-[#0A66C2]",
  youtube: "bg-[#F1C24B]",
};

function EmptyState() {
  return (
    <div className="rounded-lg bg-lm-surface border border-lm-border p-8 md:p-14 text-center">
      <div className="w-16 h-16 mx-auto rounded-full bg-lm-tint2 text-lm-primary flex items-center justify-center mb-4">
        <Radar className="w-7 h-7" strokeWidth={2} />
      </div>
      <h2 className="font-display text-2xl md:text-3xl font-semibold text-lm-text tracking-tight">
        No signals yet — run your first scan
      </h2>
      <p className="mt-3 text-lm-text2 max-w-md mx-auto">
        Real dashboard stats appear the moment you run a scan. Type a query, get scored leads across
        Reddit, X, LinkedIn and YouTube in seconds.
      </p>
      <Link
        to="/app/find-customers"
        data-testid="dashboard-empty-cta"
        className="mt-6 inline-flex items-center gap-2 bg-lm-primary hover:bg-lm-primaryHover text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-all shadow-[0_10px_26px_-12px_rgba(31,74,56,0.55)] hover:-translate-y-0.5"
      >
        Start your first scan <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className={`${i < 2 ? "lg:col-span-1" : "lg:col-span-2"} h-40 rounded-lg bg-lm-tint border border-lm-border`}
          />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-60 rounded-lg bg-lm-tint border border-lm-border" />
        ))}
      </div>
    </div>
  );
}

function BrandNudgeBanner() {
  return (
    <div
      data-testid="brand-nudge-banner"
      className="mb-6 rounded-lg border border-lm-tint2 bg-gradient-to-r from-lm-tint2/60 to-lm-tint/40 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3"
    >
      <div className="w-10 h-10 rounded-lg bg-white text-lm-primary flex items-center justify-center shrink-0 border border-lm-tint2">
        <Building2 className="w-5 h-5" strokeWidth={2.2} />
      </div>
      <div className="flex-1">
        <div className="font-semibold text-lm-text mb-0.5">
          Complete your brand setup to unlock full analysis
        </div>
        <div className="text-sm text-lm-text2 leading-snug">
          You can browse the app, but scans, pain-point lenses and market analysis
          need your brand foundation first. Takes about 30 seconds.
        </div>
      </div>
      <Link
        to="/onboarding"
        data-testid="brand-nudge-cta"
        className="inline-flex items-center gap-1.5 bg-lm-primary text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-lm-primaryHover shrink-0"
      >
        Import my website
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

const TF_OPTIONS = [
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
  { id: "all", label: "All time" },
];

function TimeframePicker({ value, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);
  const cur = TF_OPTIONS.find((o) => o.id === value) || TF_OPTIONS[1];
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-lg border border-lm-border bg-lm-surface px-3 py-2 text-sm font-medium text-lm-text2 hover:text-lm-text hover:border-lm-primary/40 transition-colors"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin text-lm-primary" /> : <CalendarDays className="w-4 h-4 text-lm-primary" />}
        {cur.label}
        <ChevronDown className={`w-3.5 h-3.5 text-lm-text3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 w-40 bg-lm-surface border border-lm-border rounded-lg shadow-lg py-1 z-30 animate-[fadeIn_0.15s_ease]">
          {TF_OPTIONS.map((o) => (
            <button
              key={o.id}
              onClick={() => { onChange(o.id); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                o.id === value ? "bg-lm-tint2 text-lm-primary font-medium" : "text-lm-text2 hover:bg-lm-tint hover:text-lm-text"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  // Phase 5.6 — workspace comes from CONTEXT, not from a private copy.
  //
  // This page used to hold its own `useState` + `[]`-deps fetch of
  // /workspaces/current, and its <h1> rendered that copy. With the switcher's
  // full page reload gone, that copy never refreshed: after a switch the header
  // showed the PREVIOUS tenant's name above the NEW tenant's stats. Two tenants
  // on one screen, each individually plausible.
  //
  // The brand had the same shape and a worse bug: a local `dashBrand` fetch that
  // only synced into context `if (confirmed && !ctxBrand)`, so once context held
  // A's brand the sync short-circuited and the two diverged permanently. Both
  // local copies are gone. AuthContext derives the brand once, re-derives it on
  // every switch, and this page reads it — one source of truth per concept.
  const {
    user,
    workspace,
    brand,
    brandLoading,
    workspaceStatus,
    workspaceEpoch,
  } = useAuth();
  // §6: Tier 1 suppresses Tier 2 and 3 messaging entirely. When the workspace
  // itself is down, this page failing is a SYMPTOM, not news — and a second
  // error surface next to the banner reads as two separate things being broken.
  const wsDown = workspaceStatus === "retrying" || workspaceStatus === "unreachable";
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState("30d");
  // Bumped by the "Try again" button. This used to be window.location.reload():
  // a whole-app restart to re-run one GET, and — more to the point — a reload in
  // an error path is exactly the thing that hides an epoch bug behind a remount.
  // A nonce keeps the single load effect as the only loader.
  const [retryNonce, setRetryNonce] = useState(0);
  const [rangeLoading, setRangeLoading] = useState(false);
  const firstRange = useRef(true);

  // Refetch just the stats when the time-frame changes (skip the initial render).
  useEffect(() => {
    if (firstRange.current) { firstRange.current = false; return; }
    // No tenant, no numbers. Also stops the setRange("30d") below from firing a
    // request into the gap between two workspaces.
    if (!workspace?.workspace_id) return undefined;
    let alive = true;
    setRangeLoading(true);
    // brand_id, or the card states THIS brand's name over EVERY brand's numbers.
    // The endpoint has accepted the filter since the backend was scoped; the
    // client simply never sent it, so a second brand silently showed the first
    // brand's totals — which is what it did the first time two brands existed.
    api.get("/dashboard/stats", { params: { window: range, brand_id: brand?.brand_id } })
      .then(({ data }) => { if (alive) setStats(data); })
      .catch(() => {})
      .finally(() => { if (alive) setRangeLoading(false); });
    return () => { alive = false; };
  }, [range, workspace?.workspace_id, brand?.brand_id]);

  useEffect(() => {
    let alive = true;
    // Fail closed while the tenant is unknown: no workspace means no stats. The
    // previous tenant's numbers must never survive into the next one's screen,
    // and `loading` is already the honest thing to render here.
    if (!workspace?.workspace_id) {
      setStats(null);
      setLoading(true);
      setError(null);
      // The load below always asks for 30d. Leaving the picker on the previous
      // tenant's selection would label the new tenant's 30-day numbers "7 days"
      // — a wrong number by way of a wrong label, which is the same defect.
      setRange("30d");
      return undefined;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data: s } = await api.get("/dashboard/stats", {
          params: { window: "30d", brand_id: brand?.brand_id },
        });
        if (!alive) return;
        setStats(s);
      } catch (e) {
        // Mid-repair, not broken. A 409 means the interceptor is re-syncing the
        // workspace; the epoch bump re-runs this effect. Showing the error card
        // here would name a failure that is already being corrected.
        if (isWorkspaceMismatch(e)) return;
        // The server's `detail` must NEVER reach a screen (§5). It is written
        // for a log, it is not keyed to any state we control, and this line
        // printed it verbatim into a red box on the customer's dashboard.
        // Real error → console. User → our words, or nothing at all.
        // eslint-disable-next-line no-console
        console.error("[dashboard] load failed", e?.response?.status, e?.response?.data?.detail);
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  // The suppression is GONE, and that is the point of Phase 5.6. It existed to
  // hold a mount-once fetch in place, and it was only ever correct because a
  // workspace switch reloaded the whole page.
  //
  // `workspaceEpoch` is the dependency that replaces the reload: it changes
  // exactly when the tenant does, so these stats re-fetch on a switch and at no
  // other time. `workspace?.workspace_id` is listed alongside it because it is
  // what the effect actually reads. Neither one loops — nothing in this effect
  // writes either of them, which is precisely what was wrong with the ctxBrand
  // version that used to live here.
  // brand_id in the deps, not just the epoch: the epoch bump and the newly
  // seated brand land in the same commit, so relying on the epoch alone would
  // refetch with whichever brand the closure captured first.
  }, [workspace?.workspace_id, workspaceEpoch, brand?.brand_id, retryNonce]);

  const isEmpty = stats?.is_empty;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-lm-text">
            {workspace?.name || "Your workspace"}
          </h1>
        </div>
          <div className="flex items-center gap-3">
            <TimeframePicker value={range} onChange={setRange} loading={rangeLoading} />
            <Link
              to="/app/find-customers"
              data-testid="dashboard-new-scan"
              className="inline-flex items-center gap-1.5 bg-lm-primary hover:bg-lm-primaryHover text-white text-sm font-semibold px-4 py-2 rounded-md transition-all shadow-[0_6px_18px_-6px_rgba(31,74,56,0.5)] hover:-translate-y-0.5"
            >
              New scan
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* `workspace &&` is the tenancy guard, not a cosmetic one: WelcomeCard
            names the workspace and grades its setup, so it must not render at
            all while we are between tenants. */}
        {!brandLoading && workspace && (
          <WelcomeCard
            workspace={workspace}
            brand={brand}
            hasFirstScan={(stats?.totals?.active_queries || 0) > 0}
            userName={user?.name}
          />
        )}
        {/* Fallback nudge: brand missing but WelcomeCard hidden because the
            workspace itself isn't there yet. */}
        {!brandLoading && !brand && !workspace && <BrandNudgeBanner />}

        {loading && <Skeleton />}
        {/* Tier 2 (§6): section-scoped, its own small "Try again", never red,
            never the top bar — and silent entirely while Tier 1 owns the
            screen. The old red box printed the server's raw `detail`; that is
            gone for good. */}
        {!loading && error && !wsDown && (
          <div
            data-testid="dashboard-load-failed"
            className="rounded-lg border border-lm-border bg-lm-tint p-4 text-sm text-lm-text2 flex flex-wrap items-center justify-between gap-3"
          >
            <span>We couldn't load your dashboard figures just now.</span>
            <button
              type="button"
              onClick={() => setRetryNonce((n) => n + 1)}
              data-testid="dashboard-load-retry"
              className="inline-flex items-center rounded-md border border-lm-border bg-lm-surface px-3 min-h-[44px] text-sm font-medium text-lm-text hover:bg-lm-tint transition-colors"
            >
              Try again
            </button>
          </div>
        )}
        {!loading && !error && isEmpty && <EmptyState />}
        {!loading && !error && !isEmpty && stats && (
          <DashboardBody stats={stats} workspace={workspace} />
        )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, spark, sparkColor, accent }) {
  return (
    <div className="rounded-xl bg-lm-surface border border-lm-border p-5 flex flex-col justify-between">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">{label}</div>
          <div className="mt-1 text-4xl font-display font-bold tracking-tight text-lm-text">{value}</div>
          <div className="text-xs text-lm-text2 mt-0.5">{sub}</div>
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${accent || "bg-lm-tint2 text-lm-primary"}`}>
          <Icon className="w-4 h-4" strokeWidth={2} />
        </div>
      </div>
      {spark && (
        <div className="h-9 mt-3">
          <SparkArea data={spark} color={sparkColor} />
        </div>
      )}
    </div>
  );
}

function DashboardBody({ stats, workspace }) {
  const activeQueries = stats.totals.active_queries;
  const activeSignals = stats.totals.active_signals;
  const savedLeads = stats.totals.saved_leads || 0;

  // Scans card — entitlement-aware. Free users get NO daily allowance: it's a
  // one-time trial (1 scan, lifetime, view-only). Paid users see the monthly
  // allowance. (Old "X/3 daily" was wrong for the 2026 hard-trial free tier.)
  const ent = workspace?.entitlements || {};
  const usage = workspace?.usage_today || {};
  // `ent.is_paid` is undefined until the workspace loads, and undefined is
  // falsy — so this card rendered "Free scan / Used / upgrade" to a paying
  // customer for the whole fetch window. Unknown is its own state: show the
  // label with no claim rather than the wrong claim.
  const planKnown = typeof ent.is_paid === "boolean";
  const scanCard = !planKnown
    ? { label: "Scans", value: "—", sub: "checking your plan…" }
    : ent.is_paid
    ? {
        label: "Scans left this month",
        value: Number.isFinite(usage.allowance_balance) ? usage.allowance_balance : "—",
        sub: !Number.isFinite(usage.allowance_cap)
          ? ""
          : usage.allowance_period_end
          ? `of ${usage.allowance_cap} · resets ${new Date(
              usage.allowance_period_end
            ).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
          : `of ${usage.allowance_cap} this month`,
      }
    : {
        label: "Free scan",
        value: ent.can_scan ? `${planNumbers().freeScans} left` : "Used",
        sub: ent.can_scan ? freeTrialBlurb().toLowerCase() : `upgrade for ${starterScansPhrase()}`,
      };
  const trend = stats.trend || [];
  const trendPct =
    trend.length > 1 && trend[0].v > 0
      ? Math.round(((trend[trend.length - 1].v - trend[0].v) / trend[0].v) * 100)
      : trend.length > 1 && trend[trend.length - 1].v > 0
        ? 100
        : 0;
  return (
    <>
      {/* Row 1 — 4 KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div data-testid="dashboard-active-queries">
          <StatCard icon={Zap} label="Scans run" value={activeQueries} sub={(stats.range_label || "All time").toLowerCase()}
            spark={stats.sparks.queries} sparkColor="#1F4A38" accent="bg-lm-tint2 text-lm-primary" />
        </div>
        <div data-testid="dashboard-active-signals">
          <StatCard icon={Radar} label="Leads found" value={activeSignals} sub="scored signals"
            spark={stats.sparks.signals} sparkColor="#D95A40" accent="bg-orange-100 text-lm-secondary" />
        </div>
        <StatCard icon={Bookmark} label="Saved leads" value={savedLeads} sub="in your pipeline"
          accent="bg-blue-100 text-blue-600" />
        <StatCard icon={PlayCircle} label={scanCard.label} value={scanCard.value}
          sub={scanCard.sub}
          accent="bg-green-100 text-green-600" />
      </div>

      {/* Row 2 — big trend chart + weekly bars */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 rounded-xl bg-lm-surface border border-lm-border p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-mono uppercase tracking-widest text-lm-text2">Signal growth trend</div>
            <span className="text-[10px] font-mono text-lm-primary px-2 py-0.5 rounded bg-lm-tint2 border border-lm-tint2 inline-flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              {trendPct >= 0 ? "+" : ""}{trendPct}%
            </span>
          </div>
          <div className="h-52">
            <AreaLineChart points={stats.trend} />
          </div>
        </div>
        <div className="rounded-xl bg-lm-surface border border-lm-border p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-mono uppercase tracking-widest text-lm-text2">Leads saved</div>
            <span className="text-[10px] font-mono text-lm-text3 px-2 py-0.5 rounded bg-lm-tint border border-lm-border">{stats.range_label || "All time"}</span>
          </div>
          <div className="h-52">
            <BarSeries data={stats.bars30} />
          </div>
        </div>
      </div>

      {/* Row 3 — feed + breakdown + prospects */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Lead intelligence feed */}
        <div className="rounded-lg bg-lm-surface border border-lm-border p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-mono uppercase tracking-widest text-lm-text2 inline-flex items-center gap-1.5">
              <Radar className="w-3.5 h-3.5 text-lm-primary" />
              Lead intelligence feed
            </div>
            <span className="text-[10px] font-mono text-lm-text3 px-2 py-0.5 rounded bg-lm-tint border border-lm-border">
              Recent
            </span>
          </div>
          <div className="space-y-3">
            {stats.feed.length === 0 && (
              <div className="text-sm text-lm-text3">No signals yet.</div>
            )}
            {stats.feed.map((f, i) => {
              const bg = PLATFORM_INITIAL_BG[(f.platform || "").toLowerCase()] || "bg-lm-primary";
              return (
                <div key={i} className="flex items-start gap-3">
                  <div
                    className={`w-9 h-9 rounded-md shrink-0 flex items-center justify-center text-white font-display font-bold text-xs ${bg}`}
                  >
                    {f.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-lm-text truncate">{f.name}</div>
                    <div className="text-xs text-lm-text2 leading-snug line-clamp-2">{f.note}</div>
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-lm-tint2 text-lm-primary shrink-0">
                    {f.tag}
                  </span>
                </div>
              );
            })}
          </div>
          <Link
            to="/app/leads"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-lm-primary hover:text-lm-primaryHover"
          >
            See full feed <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Source breakdown donut */}
        <div className="rounded-lg bg-lm-surface border border-lm-border p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-mono uppercase tracking-widest text-lm-text2">Source breakdown</div>
            <span className="text-[10px] font-mono text-lm-text3 px-2 py-0.5 rounded bg-lm-tint border border-lm-border">
              {stats.range_label || "All time"}
            </span>
          </div>
          {stats.breakdown.length === 0 ? (
            <div className="text-sm text-lm-text3">No sources yet.</div>
          ) : (
            <SourceBreakdown segments={stats.breakdown} />
          )}
        </div>

        {/* Top prospects */}
        <div className="rounded-lg bg-lm-surface border border-lm-border p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-mono uppercase tracking-widest text-lm-text2 inline-flex items-center gap-1.5">
              <Award className="w-3.5 h-3.5 text-lm-primary" />
              Top prospects
            </div>
            <span className="text-[10px] font-mono text-lm-text3 px-2 py-0.5 rounded bg-lm-tint border border-lm-border">
              By score
            </span>
          </div>
          <div className="space-y-3">
            {stats.prospects.length === 0 && (
              <div className="text-sm text-lm-text3">No prospects yet.</div>
            )}
            {stats.prospects.map((p) => {
              const bg = PLATFORM_INITIAL_BG[(p.platform || "").toLowerCase()] || "bg-lm-primary";
              const initials = p.name
                .split(/\s+/)
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase();
              return (
                <div key={p.name} className="flex items-center gap-3">
                  <span
                    className={`w-9 h-9 rounded-full ${bg} text-white font-display font-bold text-xs flex items-center justify-center shrink-0`}
                  >
                    {initials}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-lm-text truncate">{p.name}</div>
                    <div className="text-[11px] text-lm-text3 font-mono truncate">{p.handle}</div>
                  </div>
                  <span className="text-[11px] font-mono font-semibold text-lm-primary bg-lm-tint2 px-2 py-1 rounded shrink-0">
                    {p.meta}
                  </span>
                </div>
              );
            })}
          </div>
          <Link
            to="/app/leads"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-lm-primary hover:text-lm-primaryHover"
          >
            View all prospects <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Row 4 — quick actions + lens shortcuts */}
      <div className="rounded-xl bg-gradient-to-br from-lm-tint/60 to-lm-surface border border-lm-border p-5">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-lm-primary" />
          <div className="text-xs font-mono uppercase tracking-widest text-lm-text2">Jump back in</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <ActionTile to="/app/find-customers" icon={Search} label="Find Customers" primary />
          <ActionTile to="/app/find-customers?lens=pain_points" icon={HeartPulse} label="Pain Points" />
          <ActionTile to="/app/find-customers?lens=market" icon={LineChart} label="Market Insights" comingSoon />
          <ActionTile to="/app/find-customers?lens=partners" icon={Handshake} label="Find Partners" comingSoon />
          <ActionTile to="/app/saved-leads" icon={Bookmark} label="Saved Leads" />
          <ActionTile to="/app/brand" icon={Building2} label="Brand" />
        </div>
      </div>
    </>
  );
}

function ActionTile({ to, icon: Icon, label, primary, comingSoon }) {
  // Coming-soon tiles are shown but not clickable.
  if (comingSoon) {
    return (
      <div
        title={`${label} — coming soon`}
        className="relative flex flex-col items-center justify-center gap-2 rounded-xl border border-lm-border bg-lm-surface p-4 text-center text-lm-text3 cursor-not-allowed opacity-70"
      >
        <span className="absolute top-1.5 right-1.5 rounded-full bg-lm-tint2 px-1.5 py-0.5 text-[8px] font-mono uppercase tracking-wide text-lm-text3">Soon</span>
        <Icon className="w-5 h-5" strokeWidth={1.9} />
        <span className="text-xs font-medium leading-tight">{label}</span>
      </div>
    );
  }
  return (
    <Link
      to={to}
      className={`group flex flex-col items-center justify-center gap-2 rounded-xl border p-4 text-center transition-all hover:-translate-y-0.5 ${
        primary
          ? "border-lm-primary/40 bg-lm-primary text-white hover:bg-lm-primaryHover"
          : "border-lm-border bg-lm-surface text-lm-text2 hover:border-lm-primary/40 hover:text-lm-text"
      }`}
    >
      <Icon className="w-5 h-5" strokeWidth={1.9} />
      <span className="text-xs font-medium leading-tight">{label}</span>
    </Link>
  );
}
